import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Grid, List, Archive, ChevronDown, ChevronUp } from 'lucide-react';
import { ProjectCard } from '../../components/projects/ProjectCard';
import { CreateProjectModal } from '../../components/projects/CreateProjectModal';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { projectService } from '../../services/project.service';
import { Project } from '../../types/project.types';
import LoadingSpinner from '../../components/shared/LoadingSpinner';

export const ProjectsPage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const { refreshProjects: refreshNavProjects } = useProject();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [projectStats, setProjectStats] = useState<Record<string, any>>({});

  const loadProjects = async () => {
    if (!currentUser || !userProfile) return;

    setLoading(true);
    try {
      let projectData: Project[] = [];

      // Provider_admin sees ALL projects across all organizations
      if (userProfile.role === 'provider_admin') {
        projectData = await projectService.getAllProjects();
      }
      // Client_admin sees their organization's projects
      else if (userProfile.role === 'client_admin' && userProfile.organizationId) {
        projectData = await projectService.getOrganizationProjects(userProfile.organizationId);
      } else {
        // Other users (including provider_user) see only projects they're assigned to
        projectData = await projectService.getUserProjects(currentUser.uid);
      }

      setProjects(projectData);

      // Load stats for each project
      projectData.forEach(async (project) => {
        try {
          const stats = await projectService.getProjectStats(project.id);
          setProjectStats(prev => ({
            ...prev,
            [project.id]: stats
          }));
        } catch (error) {
          console.error('Error loading project stats:', error);
        }
      });
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, [currentUser, userProfile]);

  // Separate active and archived projects
  const activeProjects = projects.filter(p => p.status !== 'archived');
  const archivedProjects = projects.filter(p => p.status === 'archived');

  const filteredActiveProjects = activeProjects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredArchivedProjects = archivedProjects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const canCreateProject = userProfile?.role === 'provider_admin' ||
                          userProfile?.role === 'client_admin';

  // Provider users see project notes, others see leads
  const handleProjectClick = (projectId: string) => {
    if (userProfile?.role === 'provider_admin' || userProfile?.role === 'provider_user') {
      navigate(`/project-notes?projectId=${projectId}`);
    } else {
      navigate(`/leads?projectId=${projectId}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
          {canCreateProject && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="mr-2 h-4 w-4" />
              New Project
            </button>
          )}
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {viewMode === 'grid' ? <List className="h-5 w-5" /> : <Grid className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Active Projects Grid/List */}
      {filteredActiveProjects.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">No active projects found</p>
          {canCreateProject && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create your first project
            </button>
          )}
        </div>
      ) : (
        <div className={viewMode === 'grid'
          ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
          : 'space-y-4'
        }>
          {filteredActiveProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              stats={projectStats[project.id]}
              onClick={() => handleProjectClick(project.id)}
              onUpdate={async () => {
                // Reload projects when updated
                if (currentUser && userProfile) {
                  try {
                    let projectData: Project[] = [];
                    if (userProfile.role === 'provider_admin') {
                      projectData = await projectService.getAllProjects();
                    } else if (userProfile.role === 'client_admin' && userProfile.organizationId) {
                      projectData = await projectService.getOrganizationProjects(userProfile.organizationId);
                    } else {
                      projectData = await projectService.getUserProjects(currentUser.uid);
                    }
                    setProjects(projectData);
                    refreshNavProjects(); // Refresh navigation project selector
                    // Reload stats
                    projectData.forEach(async (proj) => {
                      try {
                        const stats = await projectService.getProjectStats(proj.id);
                        setProjectStats(prev => ({
                          ...prev,
                          [proj.id]: stats
                        }));
                      } catch (error) {
                        console.error('Error loading project stats:', error);
                      }
                    });
                  } catch (error) {
                    console.error('Error reloading projects:', error);
                  }
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Archived Projects Section */}
      {archivedProjects.length > 0 && (
        <div className="mt-12">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 group"
          >
            <Archive className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
            <span className="font-medium">Archived Projects</span>
            <span className="text-sm text-gray-400">({archivedProjects.length})</span>
            {showArchived ? (
              <ChevronUp className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            )}
          </button>

          {showArchived && (
            <div className={`${viewMode === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
              : 'space-y-4'
            } opacity-75`}>
              {filteredArchivedProjects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  stats={projectStats[project.id]}
                  onClick={() => handleProjectClick(project.id)}
                  onUpdate={async () => {
                    // Reload projects when updated
                    if (currentUser && userProfile) {
                      try {
                        let projectData: Project[] = [];
                        if (userProfile.role === 'provider_admin') {
                          projectData = await projectService.getAllProjects();
                        } else if (userProfile.role === 'client_admin' && userProfile.organizationId) {
                          projectData = await projectService.getOrganizationProjects(userProfile.organizationId);
                        } else {
                          projectData = await projectService.getUserProjects(currentUser.uid);
                        }
                        setProjects(projectData);
                        refreshNavProjects(); // Refresh navigation project selector
                      } catch (error) {
                        console.error('Error reloading projects:', error);
                      }
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onProjectCreated={(projectId) => {
          setShowCreateModal(false);
          loadProjects(); // Refresh project list
          refreshNavProjects(); // Refresh navigation project selector
          handleProjectClick(projectId);
        }}
      />
    </div>
  );
};